import { useNavigate, Link } from 'react-router-dom';
import { Bell, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useQuery } from '@tanstack/react-query';
import { notificationsApi } from '@/lib/api';

interface TopbarProps {
  title: string;
  isMenuOpen: boolean;
  onMenuToggle: () => void;
}

export function Topbar({ title, isMenuOpen, onMenuToggle }: TopbarProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.list(1, false),
    refetchInterval: 30_000,
    enabled: !!user,
  });

  const unreadCount = data?.pagination.total ?? 0;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-14 flex-shrink-0 relative z-50 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
      {/* Left: hamburger (mobile) + logo (mobile) + page title */}
      <div className="flex items-center gap-2">
        <button
          className="sm:hidden p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
          onClick={onMenuToggle}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        {/* Logo visible on mobile only — desktop shows it in the sidebar */}
        <img src="/logo.png?v=4" alt="VantixMT" className="sm:hidden h-10 w-auto object-contain" />
        {title && <h1 className="text-lg font-semibold text-slate-800">{title}</h1>}
      </div>

      {/* Right: bell, name, logout */}
      <div className="flex items-center gap-3">
        <Link
          to="/notifications"
          className="relative inline-flex items-center justify-center w-8 h-8 rounded-full text-slate-500 hover:bg-slate-100"
        >
          <Bell size={18} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Link>

        <Link
          to="/profile"
          className="text-sm text-slate-600 hidden sm:block hover:text-brand-600 hover:underline"
        >
          {user?.full_name}
        </Link>

        <button
          onClick={handleLogout}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          title="Sign out"
        >
          <LogOut size={15} />
          <span className="hidden sm:block">Sign out</span>
        </button>
      </div>
    </header>
  );
}
